/**
 * On-Call Manager Context
 * Provides state management and API operations for on-call member management
 */

import React, { createContext, useState, useCallback, useContext, type ReactNode, useEffect } from 'react';
import { useAuth } from '@auth';
import { hasPermission } from '@auth/permissions';
import type { 
  OnCallMember,
  AvailableMember,
  OnCallTicketCheck,
  NotificationSettings,
  OnCallSmsHistoryItem
} from '../types';
import * as api from '../api';
import { useLogging } from './LoggingContext';

interface OnCallManagerContextValue {
  // State
  currentMember: OnCallMember | null;
  availableMembers: OnCallMember[];
  onCallMembers: string[];
  alwaysSendList: string[];
  notificationSettings: NotificationSettings;
  recentNotifications: OnCallTicketCheck[];
  smsHistory: OnCallSmsHistoryItem[];
  loading: boolean;
  error: string | null;

  // Member Management
  refreshCurrentMember: () => Promise<void>;
  setCurrentMember: (memberId: string) => Promise<void>;
  refreshAvailableMembers: () => Promise<OnCallMember[]>;

  // On-Call Members List
  refreshOnCallMembers: () => Promise<void>;
  updateOnCallMembers: (memberIds: string[]) => Promise<void>;

  // Always-Send List
  refreshAlwaysSendList: () => Promise<void>;
  updateAlwaysSendList: (memberIds: string[]) => Promise<void>;

  // Notification Settings
  refreshNotificationSettings: () => Promise<void>;
  updateNotificationSettings: (repeatMinutes: number | null, initialDelayMinutes: number | null) => Promise<void>;

  // Notifications
  sendTestNotification: () => Promise<void>;
  refreshRecentNotifications: () => Promise<void>;
  refreshSmsHistory: () => Promise<void>;

  // Utilities
  getMemberById: (id: string) => OnCallMember | undefined;
  hasRole: (role: string) => boolean;
}

const OnCallManagerContext = createContext<OnCallManagerContextValue | undefined>(undefined);

export const useOnCallManager = (): OnCallManagerContextValue => {
  const context = useContext(OnCallManagerContext);
  if (!context) {
    throw new Error('useOnCallManager must be used within an OnCallManagerProvider');
  }
  return context;
};

interface OnCallManagerProviderProps {
  children: ReactNode;
}

// Helper to convert AvailableMember to OnCallMember
const convertToOnCallMember = (member: AvailableMember): OnCallMember => ({
  memberId: member.memberId,
  firstName: member.firstName,
  lastName: member.lastName,
  email: member.email,
  phone: member.phone,
  fullName: `${member.firstName} ${member.lastName}`
});

export const OnCallManagerProvider: React.FC<OnCallManagerProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { info, error: logError } = useLogging();

  // State
  const [currentMember, setCurrentMemberState] = useState<OnCallMember | null>(null);
  const [availableMembers, setAvailableMembers] = useState<OnCallMember[]>([]);
  const [onCallMembers, setOnCallMembers] = useState<string[]>([]);
  const [alwaysSendList, setAlwaysSendList] = useState<string[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({ initialDelayMinutes: null, repeatMinutes: null });
  const [recentNotifications, setRecentNotifications] = useState<OnCallTicketCheck[]>([]);
  const [smsHistory, setSmsHistory] = useState<OnCallSmsHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if user has required role
  const hasRole = useCallback((role: string): boolean => {
    if (role === 'OnCall.Write') return hasPermission(user, 'oncall.write');
    if (role === 'NtForms.Developer') return hasPermission(user, 'platform.admin');
    if (role === 'ServiceDeliveryManager') return hasPermission(user, 'oncall.write');
    return false;
  }, [user]);

  // Get current member
  const refreshCurrentMember = useCallback(async () => {
    try {
      info('Fetching current on-call member');
      const memberId = await api.getCurrentOnCallMember();
      
      if (memberId) {
        // Find the full member details from availableMembers
        const memberDetails = availableMembers.find(m => m.memberId === memberId);
        if (memberDetails) {
          setCurrentMemberState(memberDetails);
          info('Current on-call member fetched', memberDetails);
        } else {
          // Member ID exists but not in available members list
          const placeholder: OnCallMember = {
            memberId,
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            fullName: memberId
          };
          setCurrentMemberState(placeholder);
          info('Current on-call member ID found but details not available', { memberId });
        }
      } else {
        setCurrentMemberState(null);
        info('No current on-call member set');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch current on-call member', err);
      setError(message);
      throw err;
    }
  }, [info, logError, availableMembers]);

  // Set current member
  const setCurrentMember = useCallback(async (memberId: string) => {
    try {
      info('Setting current on-call member', { memberId });
      setLoading(true);
      await api.setCurrentOnCallMember(memberId);
      await refreshCurrentMember();
      info('Current on-call member set successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to set current on-call member', err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [info, logError, refreshCurrentMember]);

  // Get available members
  const refreshAvailableMembers = useCallback(async () => {
    try {
      info('Fetching available members');
      const apiMembers = await api.getAvailableMembers();
      const members = apiMembers.map(convertToOnCallMember);
      setAvailableMembers(members);
      info(`Fetched ${members.length} available members`);
      return members; // Return for use in initial load
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch available members', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Get on-call members list
  const refreshOnCallMembers = useCallback(async () => {
    try {
      info('Fetching on-call members list');
      const memberIds = await api.getOnCallMembers();
      setOnCallMembers(memberIds);
      info(`Fetched ${memberIds.length} on-call members`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch on-call members list', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Update on-call members list
  const updateOnCallMembers = useCallback(async (memberIds: string[]) => {
    try {
      info('Updating on-call members list', { memberIds });
      setLoading(true);
      await api.setOnCallMembers(memberIds);
      await refreshOnCallMembers();
      info('On-call members list updated successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to update on-call members list', err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [info, logError, refreshOnCallMembers]);

  // Get always-send list
  const refreshAlwaysSendList = useCallback(async () => {
    try {
      info('Fetching always-send list');
      const memberIds = await api.getAlwaysSendList();
      setAlwaysSendList(memberIds);
      info(`Fetched ${memberIds.length} always-send recipients`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch always-send list', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Update always-send list
  const updateAlwaysSendList = useCallback(async (memberIds: string[]) => {
    try {
      info('Updating always-send list', { memberIds });
      setLoading(true);
      await api.setAlwaysSendList(memberIds);
      await refreshAlwaysSendList();
      info('Always-send list updated successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to update always-send list', err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [info, logError, refreshAlwaysSendList]);

  // Get notification settings
  const refreshNotificationSettings = useCallback(async () => {
    try {
      info('Fetching notification settings');
      const [repeatMinutes, initialDelayMinutes] = await Promise.all([
        api.getNotificationRepeatMinutes(),
        api.getNotificationInitialDelayMinutes()
      ]);
      setNotificationSettings({ initialDelayMinutes, repeatMinutes });
      info('Notification settings fetched', { initialDelayMinutes, repeatMinutes });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch notification settings', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Update notification settings
  const updateNotificationSettings = useCallback(async (repeatMinutes: number | null, initialDelayMinutes: number | null) => {
    try {
      info('Updating notification settings', { initialDelayMinutes, repeatMinutes });
      setLoading(true);
      await Promise.all([
        api.setNotificationRepeatMinutes(repeatMinutes),
        api.setNotificationInitialDelayMinutes(initialDelayMinutes)
      ]);
      await refreshNotificationSettings();
      info('Notification settings updated successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to update notification settings', err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [info, logError, refreshNotificationSettings]);

  // Send test notification
  const sendTestNotification = useCallback(async () => {
    try {
      info('Sending test notification');
      setLoading(true);
      const result = await api.sendTestNotification();
      info('Test notification result', result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to send test notification', err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [info, logError]);

  // Get recent notifications
  const refreshRecentNotifications = useCallback(async () => {
    try {
      info('Fetching recent notifications');
      const notifications = await api.getRecentNotifications();
      setRecentNotifications(notifications);
      info(`Fetched ${notifications.length} recent notifications`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch recent notifications', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Get SMS history
  const refreshSmsHistory = useCallback(async () => {
    try {
      info('Fetching SMS history');
      const history = await api.getSmsHistory();
      setSmsHistory(history);
      info(`Fetched ${history.length} SMS history records`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logError('Failed to fetch SMS history', err);
      setError(message);
      throw err;
    }
  }, [info, logError]);

  // Get member by ID
  const getMemberById = useCallback((id: string): OnCallMember | undefined => {
    return availableMembers.find(m => m.memberId === id);
  }, [availableMembers]);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        const canManageOnCall = hasPermission(user, 'oncall.write');

        if (canManageOnCall) {
          // Load available members first, then use them to populate current member details
          await refreshAvailableMembers();
          await Promise.all([
            refreshOnCallMembers(),
            refreshAlwaysSendList(),
            refreshNotificationSettings(),
            refreshRecentNotifications(),
            refreshSmsHistory()
          ]);
          // Refresh current member after availableMembers is populated
          await refreshCurrentMember();
        } else {
          try {
            const memberId = await api.getCurrentOnCallMember();
            if (memberId) {
              setCurrentMemberState({
                memberId,
                firstName: memberId,
                lastName: '',
                email: '',
                phone: '',
                fullName: memberId,
              });
            } else {
              setCurrentMemberState(null);
            }
          } catch {
            setCurrentMemberState(null);
          }
          setAvailableMembers([]);
          setOnCallMembers([]);
          setAlwaysSendList([]);
          setNotificationSettings({ initialDelayMinutes: null, repeatMinutes: null });
          setRecentNotifications([]);
          setSmsHistory([]);
        }
      } catch (err) {
        logError('Failed to load initial data', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      void loadInitialData();
    }
  }, [user?.username]);

  const contextValue: OnCallManagerContextValue = {
    currentMember,
    availableMembers,
    onCallMembers,
    alwaysSendList,
    notificationSettings,
    recentNotifications,
    smsHistory,
    loading,
    error,
    refreshCurrentMember,
    setCurrentMember,
    refreshAvailableMembers,
    refreshOnCallMembers,
    updateOnCallMembers,
    refreshAlwaysSendList,
    updateAlwaysSendList,
    refreshNotificationSettings,
    updateNotificationSettings,
    sendTestNotification,
    refreshRecentNotifications,
    refreshSmsHistory,
    getMemberById,
    hasRole
  };

  return (
    <OnCallManagerContext.Provider value={contextValue}>
      {children}
    </OnCallManagerContext.Provider>
  );
};
